/**
 * Чертёж квартиры для лоадера — один на всё приложение.
 *
 * Им пользуются и 3D-лоадер в React (`BlueprintLoader`), и прелоадер в `index.html`,
 * который виден до того, как загрузится JS. Поэтому рисунок — строка SVG-разметки
 * без зависимостей: её подставляет и React, и сборка в HTML-шаблон
 * (`html.templateParameters` в `rsbuild.config.ts`). Стили — в `BlueprintLoader.css`.
 *
 * Порядок элементов задаёт порядок «рисования штрихом»: у каждого своя задержка `--i`.
 */

/** Размер чертежа в координатах `viewBox`. */
export const BLUEPRINT_VIEWBOX = "0 0 340 220";

/** Элемент чертежа: тег, класс и атрибуты; у подписи — текст. */
type Part = [tag: string, className: string, attrs: Record<string, string | number>, text?: string];

const PARTS: Part[] = [
	// внешние стены
	["rect", "bp-wall", { x: 0, y: 0, width: 340, height: 220 }],
	["rect", "bp-wall", { x: 5, y: 5, width: 330, height: 210 }],
	// внутренние стены
	["path", "bp-wall", { d: "M140 5V110M140 132V215" }],
	["path", "bp-wall", { d: "M5 110H80M102 110H140" }],
	["path", "bp-wall", { d: "M140 150H240M262 150H335" }],
	["path", "bp-wall", { d: "M240 5V60M240 82V150" }],
	// окна
	["path", "bp-win", { d: "M30 0H100M30 5H100" }],
	["path", "bp-win", { d: "M260 0H310M260 5H310" }],
	["path", "bp-win", { d: "M0 150V200M5 150V200" }],
	["path", "bp-win", { d: "M335 170V200M340 170V200" }],
	// двери
	["path", "bp-door", { d: "M140 110V132" }],
	["path", "bp-arc", { d: "M140 132A22 22 0 0 0 162 110" }],
	["path", "bp-door", { d: "M80 110H102" }],
	["path", "bp-arc", { d: "M80 110A22 22 0 0 0 102 88" }],
	["path", "bp-door", { d: "M240 60V82" }],
	["path", "bp-arc", { d: "M240 82A22 22 0 0 1 262 60" }],
	["path", "bp-door", { d: "M240 150H262" }],
	["path", "bp-arc", { d: "M240 150A22 22 0 0 1 262 172" }],
	// спальня: кровать
	["rect", "bp-furn", { x: 18, y: 18, width: 64, height: 72, rx: 3 }],
	["path", "bp-furn", { d: "M24 24H50V36H24ZM52 24H78V36H52Z" }],
	["path", "bp-furn", { d: "M18 48H82" }],
	// гостиная: диван и столик
	["rect", "bp-furn", { x: 18, y: 134, width: 84, height: 30, rx: 5 }],
	["path", "bp-furn", { d: "M46 134V164M74 134V164" }],
	["circle", "bp-furn", { cx: 60, cy: 190, r: 10 }],
	// кухня
	["path", "bp-furn", { d: "M150 14H230V48H198V14" }],
	["circle", "bp-furn", { cx: 214, cy: 31, r: 7 }],
	["rect", "bp-furn", { x: 160, y: 78, width: 50, height: 30, rx: 15 }],
	// санузел
	["rect", "bp-furn", { x: 252, y: 14, width: 70, height: 26, rx: 8 }],
	["path", "bp-furn", { d: "M290 60h18v12a9 9 0 0 1-18 0z" }],
	// прихожая
	["rect", "bp-furn", { x: 252, y: 160, width: 18, height: 44 }],
	// подписи
	["text", "bp-room", { x: 68, y: 82 }, "СПАЛЬНЯ"],
	["text", "bp-room", { x: 190, y: 136 }, "КУХНЯ"],
	["text", "bp-room", { x: 68, y: 126 }, "ГОСТИНАЯ"],
	["text", "bp-room", { x: 290, y: 100 }, "С/У"],
	["text", "bp-room", { x: 290, y: 194 }, "ХОЛЛ"],
];

/** Внутренность `<svg>` чертежа: элементы с классами и задержками `--i`. */
export const blueprintPlanMarkup = (): string =>
	PARTS.map(([tag, className, attrs, text], i) => {
		const attributes = Object.entries(attrs)
			.map(([name, value]) => `${name}="${value}"`)
			.join(" ");
		// У линий длина пути — 1: штрих рисуется через stroke-dashoffset от 1 до 0
		const path = text === undefined ? ' pathLength="1"' : "";
		const open = `<${tag} class="${className}" ${attributes}${path} style="--i:${i}"`;
		return text === undefined ? `${open}/>` : `${open}>${text}</${tag}>`;
	}).join("");
